import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { Card, Label, TextInput, Button, Table, Spinner, ToggleSwitch, Select } from "flowbite-react";
import { HiSearch } from "react-icons/hi";
import { toast } from "@/lib/toast";
import {
  useGetAiCostCumulativeQuery,
  useLazyGetAiCostSessionsQuery,
} from "../../api/reportsApi/reports";
import { getErrorMessage } from "../../utils/requestUtils";
import { useReportCompanyParam } from "../../hooks/useReportCompanyParam";
import type {
  AiCostModelBreakdown,
  AiCostSessionsTotals,
  AiCostSource,
} from "../../types/reports";

const fmtUsd = (n: number | undefined): string =>
  `$${(Number(n) || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 6,
  })}`;

const fmtInt = (n: number | undefined): string =>
  (Number(n) || 0).toLocaleString();

const fmtDateTime = (iso: string): string => {
  const d = new Date(iso);
  return isNaN(d.getTime()) ? iso : d.toLocaleString();
};

interface StatCardProps {
  label: string;
  value: string;
  color?: string;
}
const StatCard: FC<StatCardProps> = ({ label, value, color = "text-gray-900 dark:text-white" }) => (
  <Card className="flex-1 min-w-[160px]">
    <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
    <p className={`text-2xl font-bold ${color}`}>{value}</p>
  </Card>
);

const ByModelTable: FC<{ byModel: Record<string, AiCostModelBreakdown> }> = ({ byModel }) => {
  const { t } = useTranslation(["reports"]);
  const rows = Object.entries(byModel || {});
  if (rows.length === 0) return null;

  return (
    <div className="overflow-x-auto">
      <Table striped>
        <Table.Head>
          <Table.HeadCell>{t("aiCost.table.model")}</Table.HeadCell>
          <Table.HeadCell>{t("aiCost.table.calls")}</Table.HeadCell>
          <Table.HeadCell>{t("aiCost.table.inputTokens")}</Table.HeadCell>
          <Table.HeadCell>{t("aiCost.table.outputTokens")}</Table.HeadCell>
          <Table.HeadCell>{t("aiCost.table.cost")}</Table.HeadCell>
        </Table.Head>
        <Table.Body className="divide-y">
          {rows.map(([model, m]) => (
            <Table.Row key={model} className="bg-white dark:border-gray-700 dark:bg-gray-800">
              <Table.Cell className="font-medium text-gray-900 dark:text-white">{model}</Table.Cell>
              <Table.Cell>{fmtInt(m.calls)}</Table.Cell>
              <Table.Cell>{fmtInt(m.input_tokens)}</Table.Cell>
              <Table.Cell>{fmtInt(m.output_tokens)}</Table.Cell>
              <Table.Cell>{fmtUsd(m.total_cost_usd)}</Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
    </div>
  );
};

const AiAgentCostReport: FC = () => {
  const { t } = useTranslation(["reports"]);

  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [source, setSource] = useState<AiCostSource>("client");
  const [grouped, setGrouped] = useState<boolean>(true);

  const companyParam = useReportCompanyParam();

  // All-time cumulative loads immediately (no date range).
  const { data: cumulativeData, isLoading: cumulativeLoading } = useGetAiCostCumulativeQuery(companyParam);

  // Per-session + range totals load on demand.
  const [triggerSessions, { data: sessionsData, isLoading: sessionsLoading, isFetching }] =
    useLazyGetAiCostSessionsQuery();

  const handleSearch = async () => {
    if (startDate && endDate && startDate > endDate) {
      toast.error(t("startDateHigherThanEndDate"));
      return;
    }
    try {
      await triggerSessions({
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        source,
        ...companyParam,
      }).unwrap();
    } catch (error: any) {
      toast.error(getErrorMessage(error));
    }
  };

  const cumulative = cumulativeData?.cumulative;
  const totals: AiCostSessionsTotals | undefined = sessionsData?.totals;
  const sessions = sessionsData?.sessions ?? [];
  const clients = sessionsData?.clients ?? [];
  const loadingSessions = sessionsLoading || isFetching;
  const hasRows = grouped ? clients.length > 0 : sessions.length > 0;

  return (
    <div className="flex flex-col gap-6 p-4">
      {/* ===== Cumulative (all-time) ===== */}
      <div>
        <h2 className="mb-1 text-lg font-semibold text-gray-900 dark:text-white">
          {t("aiCost.cumulativeHeading")}
        </h2>
        <p className="mb-3 text-sm text-gray-500 dark:text-gray-400">{t("aiCost.cumulativeHint")}</p>

        {cumulativeLoading ? (
          <Spinner />
        ) : (
          <>
            <div className="flex flex-wrap gap-4">
              <StatCard label={t("aiCost.totalCost")} value={fmtUsd(cumulative?.total_cost_usd)} color="text-green-600" />
              <StatCard label={t("aiCost.totalSessions")} value={fmtInt(cumulative?.total_sessions)} color="text-blue-600" />
              <StatCard label={t("aiCost.totalCalls")} value={fmtInt(cumulative?.total_calls)} color="text-indigo-600" />
              <StatCard label={t("aiCost.inputTokens")} value={fmtInt(cumulative?.total_input_tokens)} color="text-purple-600" />
              <StatCard label={t("aiCost.outputTokens")} value={fmtInt(cumulative?.total_output_tokens)} color="text-orange-600" />
              <StatCard label={t("aiCost.apiCost")} value={fmtUsd(cumulative?.total_api_cost_usd)} color="text-teal-600" />
            </div>
            {cumulative?.last_updated && (
              <p className="mt-2 text-xs text-gray-400">
                {t("aiCost.lastUpdated", { value: fmtDateTime(cumulative.last_updated) })}
              </p>
            )}
            {cumulative?.by_model && Object.keys(cumulative.by_model).length > 0 && (
              <div className="mt-4">
                <h3 className="mb-2 text-base font-semibold text-gray-900 dark:text-white">
                  {t("aiCost.byModel")}
                </h3>
                <ByModelTable byModel={cumulative.by_model} />
              </div>
            )}
          </>
        )}
      </div>

      <hr className="border-gray-200 dark:border-gray-700" />

      <div>
        <h2 className="mb-1 text-lg font-semibold text-gray-900 dark:text-white">
          {t("aiCost.rangeHeading")}
        </h2>
        <p className="mb-3 text-sm text-gray-500 dark:text-gray-400">{t("aiCost.rangeHint")}</p>

        <div className="flex flex-wrap items-end gap-4">
          <div>
            <Label htmlFor="ai-cost-start" value={t("startDate")} />
            <TextInput
              id="ai-cost-start"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="ai-cost-end" value={t("endDate")} />
            <TextInput
              id="ai-cost-end"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="ai-cost-source" value={t("aiCost.source")} />
            <Select
              id="ai-cost-source"
              value={source}
              onChange={(e) => setSource(e.target.value as AiCostSource)}
            >
              <option value="client">{t("aiCost.clientSessions")}</option>
              <option value="agent">{t("aiCost.agentSessions")}</option>
            </Select>
          </div>
          <Button onClick={handleSearch} disabled={loadingSessions}>
            <HiSearch className="mr-2 h-5 w-5" />
            {t("search")}
          </Button>
        </div>
      </div>

      {/* ===== Range totals ===== */}
      {totals && (
        <div className="flex flex-wrap gap-4">
          <StatCard label={t("aiCost.totalCost")} value={fmtUsd(totals.total_cost_usd)} color="text-green-600" />
          <StatCard label={t("aiCost.totalSessions")} value={fmtInt(totals.total_sessions)} color="text-blue-600" />
          <StatCard label={t("aiCost.totalCalls")} value={fmtInt(totals.total_calls)} color="text-indigo-600" />
          <StatCard label={t("aiCost.inputTokens")} value={fmtInt(totals.total_input_tokens)} color="text-purple-600" />
          <StatCard label={t("aiCost.outputTokens")} value={fmtInt(totals.total_output_tokens)} color="text-orange-600" />
        </div>
      )}

      {totals?.by_model && Object.keys(totals.by_model).length > 0 && (
        <div>
          <h3 className="mb-2 text-base font-semibold text-gray-900 dark:text-white">
            {t("aiCost.byModel")}
          </h3>
          <ByModelTable byModel={totals.by_model} />
        </div>
      )}

      <div>
        <div className="mb-2 flex flex-wrap items-center justify-between gap-3">
          <h3 className="text-base font-semibold text-gray-900 dark:text-white">
            {t("aiCost.perSession")}
          </h3>
          <ToggleSwitch
            checked={grouped}
            label={grouped ? t("aiCost.viewGrouped") : t("aiCost.viewRaw")}
            onChange={setGrouped}
          />
        </div>

        {loadingSessions ? (
          <Spinner />
        ) : !hasRows ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">{t("aiCost.noData")}</p>
        ) : grouped ? (
          <div className="overflow-x-auto">
            <Table striped>
              <Table.Head>
                <Table.HeadCell>{t("aiCost.table.client")}</Table.HeadCell>
                <Table.HeadCell>{t("aiCost.table.turns")}</Table.HeadCell>
                <Table.HeadCell>{t("aiCost.table.calls")}</Table.HeadCell>
                <Table.HeadCell>{t("aiCost.table.inputTokens")}</Table.HeadCell>
                <Table.HeadCell>{t("aiCost.table.outputTokens")}</Table.HeadCell>
                <Table.HeadCell>{t("aiCost.table.cost")}</Table.HeadCell>
                <Table.HeadCell>{t("aiCost.table.period")}</Table.HeadCell>
              </Table.Head>
              <Table.Body className="divide-y">
                {clients.map((c, idx) => (
                  <Table.Row key={`${c.client_name}-${idx}`} className="bg-white dark:border-gray-700 dark:bg-gray-800">
                    <Table.Cell className="font-medium text-gray-900 dark:text-white">
                      {c.client_name || c.client_id || "-"}
                    </Table.Cell>
                    <Table.Cell>{fmtInt(c.turns)}</Table.Cell>
                    <Table.Cell>{fmtInt(c.total_calls)}</Table.Cell>
                    <Table.Cell>{fmtInt(c.total_input_tokens)}</Table.Cell>
                    <Table.Cell>{fmtInt(c.total_output_tokens)}</Table.Cell>
                    <Table.Cell>{fmtUsd(c.total_cost_usd)}</Table.Cell>
                    <Table.Cell className="whitespace-nowrap text-xs text-gray-500">
                      {fmtDateTime(c.first_seen)} → {fmtDateTime(c.last_seen)}
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table striped>
              <Table.Head>
                <Table.HeadCell>{t("aiCost.table.dateTime")}</Table.HeadCell>
                <Table.HeadCell>{t("aiCost.table.client")}</Table.HeadCell>
                <Table.HeadCell>{t("aiCost.table.calls")}</Table.HeadCell>
                <Table.HeadCell>{t("aiCost.table.inputTokens")}</Table.HeadCell>
                <Table.HeadCell>{t("aiCost.table.outputTokens")}</Table.HeadCell>
                <Table.HeadCell>{t("aiCost.table.cost")}</Table.HeadCell>
              </Table.Head>
              <Table.Body className="divide-y">
                {sessions.map((s, idx) => (
                  <Table.Row key={`${s.session_id}-${s.timestamp}-${idx}`} className="bg-white dark:border-gray-700 dark:bg-gray-800">
                    <Table.Cell className="whitespace-nowrap">{fmtDateTime(s.timestamp)}</Table.Cell>
                    <Table.Cell className="font-medium text-gray-900 dark:text-white">
                      {s.client_name ?? s.client_id ?? "-"}
                    </Table.Cell>
                    <Table.Cell>{fmtInt(s.total_calls)}</Table.Cell>
                    <Table.Cell>{fmtInt(s.total_input_tokens)}</Table.Cell>
                    <Table.Cell>{fmtInt(s.total_output_tokens)}</Table.Cell>
                    <Table.Cell>{fmtUsd(s.total_cost_usd)}</Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AiAgentCostReport;
